/**
 * Tab Salud del Panel People: última corrida (freshness verde/ámbar/rojo,
 * patrón BrainHealthTab) + historial de corridas del orchestrator diario y del
 * People-flow.
 *
 * Feedback Sol 10/09 ("poco claro"): el badge verde decía "✓ Funcionando"
 * aunque el motor estuviera APAGADO y no hubiera mandado nada — que es
 * justamente lo que hay que ver de un vistazo. Ahora el estado del motor es lo
 * primero de la tab, y cada corrida dice si la disparó el cron o una persona.
 */

import { currentLocale } from '@/lib/locale';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Activity, AlertTriangle, CalendarClock, MapPin, Play, PowerOff, Sprout } from 'lucide-react';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { usePagination } from '@/hooks/usePagination';
import { useAuth } from '@/contexts/SharedAuthContext';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import type { PeopleFlowRunSummary } from '@/../../shared/types/people-flow';
import { stepTitle } from '@/components/users/onboarding-flow/plain-language';

/**
 * Estado de un automatismo. `off` gana sobre la frescura: un motor apagado que
 * corrió hace 5 minutos NO está funcionando, y pintarlo verde era el principal
 * malentendido de esta tab.
 */
type TFunc = ReturnType<typeof useTranslation>['t'];

function FreshnessBadge({ iso, off }: { iso: string | null; off?: boolean }) {
  const { t } = useTranslation();
  if (!iso) {
    return (
      <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
        {t('admin.onboardinghealthtab.nunca_corrio')}
      </span>
    );
  }
  if (off) {
    return (
      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
        {t('users.people_panel.health.badge_off')}
      </span>
    );
  }
  const hours = (Date.now() - new Date(iso).getTime()) / 3_600_000;
  const cls =
    hours < 25
      ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
      : hours < 49
        ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400'
        : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400';
  const label =
    hours < 1
      ? t('users.people_panel.health.badge_ok_now')
      : hours < 25
        ? t('users.people_panel.health.badge_ok_hours', { hours: Math.round(hours) })
        : hours < 49
          ? t('users.people_panel.health.badge_stale_hours', { hours: Math.round(hours) })
          : t('users.people_panel.health.badge_stale_days', { days: Math.round(hours / 24) });
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{label}</span>;
}

/**
 * Quién disparó la corrida. `performedBy` es la fuente principal, pero las
 * corridas viejas (anteriores al fix del 10/09) quedaron firmadas por el cron
 * aunque las hubiera disparado alguien: para esas cae al `trigger` del resumen,
 * que sí distingue, aunque ya no tenga el nombre de la persona.
 */
function triggerLabel(t: TFunc, run: PeopleFlowRunSummary): string {
  const k = 'users.people_panel.health';
  if (!run.performedBy.startsWith('system:')) {
    return t(`${k}.trigger_manual`, { who: run.performedBy });
  }
  if ((run.summary as Record<string, unknown>).trigger === 'manual') {
    return t(`${k}.trigger_manual_anon`);
  }
  return t(`${k}.trigger_cron`);
}

/** El motor de mensajes está apagado en esa corrida (kill-switch de config). */
const isEngineOff = (run: PeopleFlowRunSummary | null) =>
  !!run && run.kind === 'people_flow' && (run.summary as Record<string, unknown>).enabled === false;

/** Resumen de una corrida, contado en criollo (y en los 3 idiomas). */
function summaryLine(t: TFunc, run: PeopleFlowRunSummary): string {
  const s = run.summary as Record<string, unknown>;
  const k = 'users.people_panel.health.line';
  if (run.kind === 'daily') {
    const errores = (Number(s.altasErrores) || 0) + (Number(s.bajasErrores) || 0);
    const partes = [
      t(`${k}.altas`, { count: Number(s.altasProcessed) || 0 }),
      t(`${k}.bajas`, { count: Number(s.bajasProcessed) || 0 }),
    ];
    if (errores > 0) partes.push(t(`${k}.con_problemas`, { count: errores }));
    return partes.join(' · ');
  }
  if (s.enabled === false) return t(`${k}.off`);

  const sent = Number(s.sent) || 0;
  const errors = Number(s.errors) || 0;
  const expired = Number(s.expired) || 0;
  const waiting = Number(s.waiting) || 0;
  if (sent === 0 && errors === 0 && expired === 0 && waiting === 0) return t(`${k}.nothing`);

  const partes: string[] = [];
  if (sent > 0) partes.push(t(`${k}.sent`, { count: sent }));
  if (errors > 0) partes.push(t(`${k}.errors`, { count: errors }));
  if (expired > 0) partes.push(t(`${k}.expired`, { count: expired }));
  if (waiting > 0) partes.push(t(`${k}.waiting`, { count: waiting }));
  const dups = Array.isArray(s.duplicates) ? s.duplicates.length : 0;
  if (dups > 0) partes.push(t(`${k}.duplicates`, { count: dups }));
  return partes.join(' · ');
}

/** Qué mandó (y qué no) la última corrida, paso por paso y en criollo. */
function ByStepBreakdown({ run }: { run: PeopleFlowRunSummary }) {
  const { t } = useTranslation();
  const byStep = (run.summary as Record<string, unknown>).byStep as
    | Record<string, { sent?: number; errors?: number; expired?: number; waiting?: number }>
    | undefined;
  const entries = Object.entries(byStep ?? {}).filter(
    ([, v]) => (v?.sent ?? 0) + (v?.errors ?? 0) + (v?.expired ?? 0) + (v?.waiting ?? 0) > 0
  );
  if (!entries.length) return null;
  return (
    <div className="mt-2 space-y-1">
      {entries.map(([stepId, v]) => {
        const partes: string[] = [];
        if ((v.sent ?? 0) > 0) partes.push(t('admin.onboardinghealthtab.n_ok', { n: v.sent }));
        if ((v.errors ?? 0) > 0) partes.push(t('admin.onboardinghealthtab.n_con_problema', { n: v.errors }));
        if ((v.expired ?? 0) > 0) partes.push(t('admin.onboardinghealthtab.n_ya_no_aplicaba', { n: v.expired }));
        if ((v.waiting ?? 0) > 0) partes.push(t('admin.onboardinghealthtab.n_esperando_slack', { n: v.waiting }));
        const tone =
          (v.errors ?? 0) > 0
            ? 'text-red-600 dark:text-red-400'
            : (v.expired ?? 0) > 0
              ? 'text-amber-600 dark:text-amber-400'
              : 'text-emerald-600 dark:text-emerald-400';
        return (
          <div key={stepId} className="flex items-baseline justify-between gap-2 text-xs">
            <span className="truncate text-muted-foreground">{stepTitle(t, stepId)}</span>
            <span className={`shrink-0 ${tone}`}>{partes.join(' · ')}</span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Qué fila de la config le toca a cada Comunidad de la planilla. Incidente
 * 14/09: la planilla dice "Bogota" y la config "Colombia"; el Office Host de
 * Colombia y Brasil no recibió nada en un mes y ninguna pantalla lo mostraba.
 * Una fila sin Office Host acá = recordatorio del jueves que va a fallar.
 */
function ClapbaseCoverageCard() {
  const { t } = useTranslation();
  const coverage = trpc.peopleFlow.getClapbaseCoverage.useQuery(undefined, { staleTime: 60_000 });
  const rows = coverage.data ?? [];
  const missing = rows.filter((r) => !r.officeHost);
  const k = 'users.people_panel.health.coverage';
  const dash = <span className="text-muted-foreground">—</span>;

  return (
    <Card>
      <div className="border-b p-3">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          {t(`${k}.title`)}
        </h3>
        <p className="text-xs text-muted-foreground">{t(`${k}.desc`)}</p>
      </div>
      {missing.length > 0 && (
        <div className="m-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {t(`${k}.missing`, { count: missing.length, list: missing.map((m) => m.comunidad || '—').join(', ') })}{' '}
            <Link to="/admin/users/people-panel?tab=config" className="underline">
              {t(`${k}.missing_link`)}
            </Link>
          </span>
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t(`${k}.col_sheet`)}</TableHead>
            <TableHead className="text-right">{t(`${k}.col_altas`)}</TableHead>
            <TableHead>{t(`${k}.col_host`)}</TableHead>
            <TableHead>{t(`${k}.col_channels`)}</TableHead>
            <TableHead>{t(`${k}.col_ppt`)}</TableHead>
            <TableHead>{t(`${k}.col_whatsapp`)}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {coverage.isLoading && (
            <TableRow>
              <TableCell colSpan={6} className="py-6 text-center text-muted-foreground">…</TableCell>
            </TableRow>
          )}
          {!coverage.isLoading && rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="py-6 text-center text-muted-foreground">{t(`${k}.empty`)}</TableCell>
            </TableRow>
          )}
          {rows.map((r) => (
            <TableRow key={`${r.comunidad}|${r.pais}`} className={r.officeHost ? undefined : 'bg-amber-50/60 dark:bg-amber-950/30'}>
              <TableCell className="text-sm">
                <span className="font-medium">{r.comunidad || '—'}</span>
                {r.pais && <span className="ml-1 text-xs text-muted-foreground">· {r.pais}</span>}
              </TableCell>
              <TableCell className="text-right tabular-nums text-sm">{r.altas}</TableCell>
              <TableCell className="text-sm">
                {r.officeHost ? (
                  <span title={r.officeHost.email}>
                    {r.officeHost.name}
                    {r.officeHost.key.toLowerCase() !== r.comunidad.toLowerCase() && (
                      <span className="ml-1 text-xs text-muted-foreground">({r.officeHost.key})</span>
                    )}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-300">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {t(`${k}.no_host`)}
                  </span>
                )}
              </TableCell>
              <TableCell className="text-sm">{r.channels ?? dash}</TableCell>
              <TableCell className="text-sm">{r.ppt ?? dash}</TableCell>
              <TableCell className="text-sm">{r.whatsapp ?? dash}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

export function OnboardingHealthTab() {
  const { t } = useTranslation();
  const { roles, isAdmin } = useAuth();
  const canSeed = isAdmin || (roles ?? []).includes('people');
  const [seedOpen, setSeedOpen] = useState(false);
  const utils = trpc.useUtils();

  const runs = trpc.peopleFlow.getRunHistory.useQuery({ limit: 30 }, { staleTime: 60_000 });
  // Hasta 30 corridas: el hook va antes del return temprano del loader
  const { pageItems: pageRuns, footer: paginationFooter } = usePagination(runs.data ?? [], 25);

  const invalidate = () => {
    utils.peopleFlow.getRunHistory.invalidate();
    utils.peopleFlow.getFlowStatus.invalidate();
  };
  const runNow = trpc.peopleFlow.triggerHourlyRun.useMutation({
    onSuccess: (r) => {
      toast.success(
        r.enabled
          ? t('users.people_panel.health.run_done', { sent: r.sent, errors: r.errors })
          : t('users.people_panel.health.run_disabled')
      );
      invalidate();
    },
    onError: (e) => toast.error(e.message),
  });
  const seed = trpc.peopleFlow.seedPeopleFlowFlags.useMutation({
    onSuccess: (r) => {
      toast.success(t('users.people_panel.health.seed_done', { seeded: r.seeded, altas: r.altas }));
      setSeedOpen(false);
      invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  if (runs.isLoading) return <WinclapLoader />;

  const daily = runs.data?.find((r) => r.kind === 'daily') ?? null;
  const flow = runs.data?.find((r) => r.kind === 'people_flow') ?? null;

  const engineOff = isEngineOff(flow);

  return (
    <div className="space-y-4">
      {/* Lo primero de la tab: si el motor está apagado, NADA de esto sale.
          Antes había que deducirlo del texto chiquito de la card. */}
      {engineOff && (
        <Alert>
          <PowerOff className="h-4 w-4" />
          <AlertTitle>{t('users.people_panel.health.engine_off_title')}</AlertTitle>
          <AlertDescription>
            {t('users.people_panel.health.engine_off_desc')}{' '}
            <Link to="/admin/users/people-panel?tab=config" className="text-primary underline">
              {t('users.people_panel.health.engine_off_link')}
            </Link>
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5"
          disabled={runNow.isPending}
          onClick={() => runNow.mutate()}
        >
          <Play className="h-3.5 w-3.5" />
          {t('users.people_panel.health.run_now')}
        </Button>
        {canSeed && (
          <Button
            size="sm"
            variant="ghost"
            className="gap-1.5 text-muted-foreground"
            onClick={() => setSeedOpen(true)}
          >
            <Sprout className="h-3.5 w-3.5" />
            {t('users.people_panel.health.seed')}
          </Button>
        )}
      </div>

      <AlertDialog open={seedOpen} onOpenChange={setSeedOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('users.people_panel.health.seed_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('users.people_panel.health.seed_desc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('users.people_panel.detail.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={seed.isPending}
              onClick={(e) => {
                e.preventDefault();
                seed.mutate();
              }}
            >
              {t('users.people_panel.health.seed_confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <p className="text-sm text-muted-foreground">
        {t('admin.onboardinghealthtab.aca_ves_si_los_automatismos_del')}
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        <Card className="p-4">
          <p className="flex items-center gap-1.5 text-sm font-medium">
            <CalendarClock className="h-4 w-4 text-muted-foreground" />
            {t('admin.onboardinghealthtab.creacion_de_cuentas_y_bajas')}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <FreshnessBadge iso={daily?.createdAt ?? null} />
            {daily && <span className="text-xs text-muted-foreground">{summaryLine(t, daily)}</span>}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {t('admin.onboardinghealthtab.corre_todas_las_mananas')}
          </p>
        </Card>
        <Card className="p-4">
          <p className="flex items-center gap-1.5 text-sm font-medium">
            <Activity className="h-4 w-4 text-muted-foreground" />
            {t('admin.onboardinghealthtab.mensajes_y_reuniones_del_onboarding')}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <FreshnessBadge iso={flow?.createdAt ?? null} off={engineOff} />
            {flow && <span className="text-xs text-muted-foreground">{summaryLine(t, flow)}</span>}
          </div>
          {flow && <ByStepBreakdown run={flow} />}
          <p className="mt-2 text-xs text-muted-foreground">
            {t('admin.onboardinghealthtab.revisa_cada_hora_si_a_alguien')}
          </p>
        </Card>
      </div>

      <ClapbaseCoverageCard />

      <Card>
        <div className="border-b p-3">
          <h3 className="text-sm font-semibold">{t('admin.onboardinghealthtab.ultimas_revisiones_automaticas')}</h3>
          <p className="text-xs text-muted-foreground">
            {t('admin.onboardinghealthtab.cada_linea_es_una_pasada_del')}
          </p>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('admin.onboardinghealthtab.cuando')}</TableHead>
              <TableHead>{t('admin.onboardinghealthtab.que_reviso')}</TableHead>
              <TableHead>{t('users.people_panel.health.table_trigger')}</TableHead>
              <TableHead>{t('users.people_panel.health.table_summary')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(runs.data ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                  {t('admin.onboardinghealthtab.todavia_no_hay_revisiones_registradas')}
                </TableCell>
              </TableRow>
            )}
            {pageRuns.map((run) => (
              <TableRow key={`${run.kind}-${run.createdAt}`}>
                <TableCell className="whitespace-nowrap tabular-nums">
                  {new Date(run.createdAt).toLocaleString(currentLocale(), { dateStyle: 'short', timeStyle: 'short' })}
                </TableCell>
                <TableCell className="text-sm">
                  {run.kind === 'daily' ? t('admin.onboardinghealthtab.cuentas_y_bajas') : t('admin.onboardinghealthtab.mensajes_y_reuniones')}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{triggerLabel(t, run)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{summaryLine(t, run)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="px-3 pb-3">{paginationFooter}</div>
      </Card>
    </div>
  );
}
